/**
 * Persist contact edits to ContactsStaging (adds/updates AND deletes).
 * Handles phone number normalisation and trims other string fields before
 * writing. Uses global Firebase compat (window.firebase) so no bare-module
 * imports.
 */
import {
  extensionFieldForPhoneField,
  normalizePhoneNumberWithExtension,
  sanitizePhoneExtension,
} from '../utils/phoneExtension.js';

function e164(n) {
  return normalizePhoneNumberWithExtension(n).phone || '';
}

/**
 * @param {string} tenantId
 * @param {string} contactId
 * @param {{
 *   mobilePhone?: string|null|undefined,
 *   mobilePhoneExtension?: string|null|undefined,
 *   officePhone?: string|null|undefined,
 *   officePhoneExtension?: string|null|undefined,
 *   mainOfficePhone?: string|null|undefined,
 *   mainOfficePhoneExtension?: string|null|undefined,
 *   firstName?: string|null|undefined,
 *   lastName?: string|null|undefined,
 *   linkedinUrl?: string|null|undefined,
 *   email?: string|null|undefined,
 * }} changes
 */
export async function saveStagedPhones(tenantId, contactId, changes = {}) {
  const fb = /** @type {any} */ (window).firebase;
  if (!fb?.firestore)
    throw new Error(
      'Firebase compat not loaded (firebase.firestore not found)'
    );

  const db = fb.firestore();
  const ref = db
    .collection('tenants')
    .doc(tenantId)
    .collection('ContactsStaging')
    .doc(contactId);

  // Build patch: deleteField() when blank/null, else normalized value
  const del = fb.firestore.FieldValue.delete();
  const patch = {};

  const set = (k, mode = 'string') => {
    if (!(k in changes)) return; // only touch fields the user edited
    const v = changes[k];
    if (v == null) {
      patch[k] = del;
      if (mode === 'phone') {
        const extKey = extensionFieldForPhoneField(k);
        if (!(extKey in changes)) patch[extKey] = del;
      }
      return;
    }

    if (typeof v === 'string') {
      const trimmed = v.trim();
      if (!trimmed) {
        patch[k] = del;
        if (mode === 'phone') {
          const extKey = extensionFieldForPhoneField(k);
          if (!(extKey in changes)) patch[extKey] = del;
        }
        return;
      }
      if (mode === 'phone') {
        const parsed = normalizePhoneNumberWithExtension(trimmed);
        const norm = parsed.phone || e164(trimmed);
        patch[k] = norm || del;
        const extKey = extensionFieldForPhoneField(k);
        if (parsed.extension && !(extKey in changes)) {
          patch[extKey] = parsed.extension;
        }
        return;
      }
      if (mode === 'extension') {
        const ext = sanitizePhoneExtension(trimmed);
        patch[k] = ext || del;
        return;
      }
      patch[k] = trimmed;
      return;
    }

    if (mode === 'phone') {
      const norm = e164(v);
      patch[k] = norm || del;
    } else if (mode === 'extension') {
      const ext = sanitizePhoneExtension(v);
      patch[k] = ext || del;
    } else {
      patch[k] = v;
    }
  };

  set('mobilePhone', 'phone');
  set('mobilePhoneExtension', 'extension');
  set('officePhone', 'phone');
  set('officePhoneExtension', 'extension');
  set('mainOfficePhone', 'phone');
  set('mainOfficePhoneExtension', 'extension');
  set('firstName');
  set('lastName');
  set('linkedinUrl');
  set('email');

  if (contactId) {
    patch.crmContactId = contactId;
    patch.contactId = contactId;
  }

  if (!Object.keys(patch).length) return; // nothing to write
  console.log('[StagingWriter] WRITE', ref.path, patch);
  // Use set(..., { merge:true }) to work for both new/existing docs
  await ref.set(patch, { merge: true });
}
